import { useAuth, useUser } from "@clerk/clerk-react";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import posthog from "posthog-js";
import React from "react";

const TEMPLATE = "Default";

interface ClerkTokenContextType {
  token: string | null;
}

export const ClerkContext = React.createContext<ClerkTokenContextType>(null!);

export function ClerkTokenProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = React.useState<string | null>(null);

  const qc = useQueryClient();

  const { getToken } = useAuth();
  const { user } = useUser();
  const signupTrackedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!import.meta.env.PROD) return;

    if (user) {
      const email = user.primaryEmailAddress?.emailAddress;
      const username = user.username ?? email ?? undefined;

      const activeOrgId =
        (user.publicMetadata as { activeOrgId?: string } | null)?.activeOrgId ??
        user.organizationMemberships?.[0]?.organization?.id;
      const activeOrgName = user.organizationMemberships?.find(
        (m) => m.organization.id === activeOrgId,
      )?.organization?.name;

      posthog.identify(user.id, {
        email,
        name: user.fullName ?? undefined,
        username,
        org_id: activeOrgId,
      });

      if (activeOrgId) {
        posthog.group(
          "organization",
          activeOrgId,
          activeOrgName ? { name: activeOrgName } : undefined,
        );
      }

      // Fire `signup_completed` once if Clerk just minted this user. Clerk's
      // SignUp component redirects post-success but doesn't expose a callback;
      // recency on createdAt is the cleanest stand-in. 60s window absorbs slow
      // redirects and the post-signup org-chooser hop.
      if (
        signupTrackedRef.current !== user.id &&
        user.createdAt &&
        Date.now() - new Date(user.createdAt).getTime() < 60_000
      ) {
        posthog.capture("signup_completed", {
          email,
          org_id: activeOrgId,
        });
        signupTrackedRef.current = user.id;
      }

      Sentry.setUser({
        id: user.id,
        email,
        username,
      });
    } else {
      signupTrackedRef.current = null;
      posthog.reset();
    }
  }, [user]);

  React.useEffect(() => {
    const _getToken = async () => {
      setToken(await getToken({ template: TEMPLATE }));
      qc.invalidateQueries();
    };

    _getToken();

    // Refresh token every 6h
    const interval = setInterval(_getToken, 60 * 60 * 1000 * 6);

    return () => clearInterval(interval);
  }, [getToken, qc]);

  return <ClerkContext.Provider value={{ token }}>{children}</ClerkContext.Provider>;
}
